import { cn } from "@semo/shared-ui";

/**
 * 계약 대금이 어디까지 갔는지. 채널마다 '완료' 를 뜻하는 축이 다르다.
 *
 *  - 카드: 세모가 결제 승인 통지를 받아 `paid_at` 을 찍는다 → `isPaid`.
 *  - 현금: 세모는 입금을 추적하지 않는다. 대신 씨마켓이 알려 주는 **계산서 진행 상태**가
 *    같은 자리를 맡는다 → `taxInvoiceStatus`.
 *
 * 두 축을 한 곳에서 라벨로 옮겨, 계약 카드와 서류 관리 카드가 같은 말을 쓰게 한다.
 */
export type ContractPaymentChannel = "cash" | "card";
export type ContractTaxInvoiceStatus = "REQUESTED" | "ISSUED" | "CANCELLED";

export interface ContractSettlementBadge {
  label: string;
  /** done=끝난 일(초록), progress=진행 중(주황). */
  tone: "done" | "progress";
}

/**
 * 배지를 만든다. **아직 아무 일도 일어나지 않았으면 `null`** — 그때는 배지를 그리지 않는다.
 *
 * `CANCELLED` 도 `null` 이다. 씨마켓은 발행취소를 통지하지 않으므로(관리번호만 조용히
 * 지운다) 이 값은 평시에 오지 않는다. 혹시 오더라도 "취소됨" 을 띄우는 대신 요청 전과
 * 같은 화면으로 되돌리는 편이 낫다 — 그 상태에서 할 일이 '다시 요청' 으로 같기 때문이다.
 */
export function resolveContractSettlementBadge(params: {
  paymentChannel: ContractPaymentChannel | null;
  isPaid?: boolean;
  taxInvoiceStatus?: ContractTaxInvoiceStatus | null;
}): ContractSettlementBadge | null {
  const { paymentChannel, isPaid = false, taxInvoiceStatus = null } = params;

  if (paymentChannel === "card") {
    return isPaid ? { label: "결제 완료", tone: "done" } : null;
  }
  if (paymentChannel === "cash") {
    if (taxInvoiceStatus === "ISSUED") {
      return { label: "발행 완료", tone: "done" };
    }
    if (taxInvoiceStatus === "REQUESTED") {
      return { label: "발행 요청됨", tone: "progress" };
    }
  }
  return null;
}

export function ContractSettlementBadgeChip({
  badge,
  className,
}: {
  badge: ContractSettlementBadge;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "shrink-0 rounded-full border px-2 py-0.5 text-2xs font-semibold",
        badge.tone === "done"
          ? "border-emerald-200 bg-emerald-50 text-emerald-700"
          : "border-amber-200 bg-amber-50 text-amber-700",
        className,
      )}
    >
      {badge.label}
    </span>
  );
}
